import { max } from 'd3-array';
import { useEffect, useMemo, useState } from 'react';
import { scaleBand, scaleLinear } from 'd3-scale';
import { barStyle, chartColors, chartFont } from '../../../design/charts.js';
import { fmtInt, fmtValue, tickSpecifier } from '../lib/format';
import { Tooltip, useTooltip } from './Tooltip';
import { useMeasure } from './useMeasure';
import { t } from '../i18n';

export interface BarRow { key: string; label: string; value: number; n: number }

interface Props {
  rows: BarRow[];
  units: string | null;
  valueLabel: string;      // e.g. "Median (2023)"
  highlightKey?: string | null;
  height?: number;
}

const M = { top: 8, right: 96, bottom: 34 };
const BAR = 22;            // <= 24px per mark spec
const ROW = 44;
const ROW_WRAPPED = 58;    // room for a two-line label plus the n line
const LABEL_FONT = '500 13.5px "Inter Variable", Inter, system-ui, sans-serif';
const LABEL_GAP = 14;      // between label and axis
const LABEL_LINE = 15;

let ctx: CanvasRenderingContext2D | null = null;
function textWidth(s: string) {
  ctx ??= document.createElement('canvas').getContext('2d');
  if (!ctx) return s.length * 7.5;
  ctx.font = LABEL_FONT;
  return ctx.measureText(s).width;
}

/** Greedy word wrap into at most two lines; the second gets an ellipsis if it still overflows. */
function wrapLabel(label: string, maxW: number): string[] {
  if (textWidth(label) <= maxW) return [label];
  const words = label.split(' ');
  let first = words[0]!;
  let i = 1;
  while (i < words.length && textWidth(`${first} ${words[i]}`) <= maxW) first += ` ${words[i++]}`;
  let second = words.slice(i).join(' ');
  if (!second) return [first];
  if (textWidth(second) > maxW) {
    while (second.length > 1 && textWidth(`${second}…`) > maxW) second = second.slice(0, -1).trimEnd();
    second += '…';
  }
  return [first, second];
}

/** Horizontal bars, one per region, sorted by the API. Value at the tip, n under the label. */
export function BarChart({ rows, units, valueLabel, highlightKey = null, height = 300 }: Props) {
  const { ref, width } = useMeasure();
  const { tip, show, showAt, hide } = useTooltip();
  const [hover, setHover] = useState<string | null>(null);
  const leave = () => { hide(); setHover(null); };
  // measure again once the web font is in; the first pass may have used the fallback face
  const [fontsReady, setFontsReady] = useState(() => document.fonts?.status === 'loaded');
  useEffect(() => { if (!fontsReady) void document.fonts?.ready.then(() => setFontsReady(true)); }, [fontsReady]);
  // label column as wide as the longest label, but never more than 40% of the chart
  const { left, lines, row } = useMemo(() => {
    const cap = Math.max(90, width * 0.4);
    const longest = Math.max(0, ...rows.map((r) => textWidth(r.label)));
    const labelW = Math.min(longest, cap - LABEL_GAP);
    const lines = new Map(rows.map((r) => [r.key, wrapLabel(r.label, labelW)]));
    const wrapped = [...lines.values()].some((l) => l.length > 1);
    return { left: labelW + LABEL_GAP, lines, row: wrapped ? ROW_WRAPPED : ROW };
  }, [rows, width, fontsReady]);
  const innerW = Math.max(120, width - left - M.right);
  const plotH = Math.max(120, height - M.top - M.bottom);

  const xMax = max(rows, (r) => r.value) ?? 0;
  const x = scaleLinear().domain([Math.min(0, ...rows.map((r) => r.value)), xMax || 1]).nice().range([0, innerW]);
  const y = scaleBand<string>().domain(rows.map((r) => r.key)).range([0, plotH]).paddingInner(row === ROW ? 0.45 : 0.55);
  const ticks = x.ticks(4);
  const tf = x.tickFormat(4, tickSpecifier(units));
  const x0 = x(0);

  return (
    <div className="chart" ref={ref}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label={t.barsAria(valueLabel)}>
          <g transform={`translate(${left},${M.top})`}>
            {ticks.map((t) => (
              <g key={t} transform={`translate(${x(t)},0)`}>
                <line y1={0} y2={plotH} stroke={chartColors.grid} />
                <text y={plotH + 20} textAnchor="middle" fill={chartColors.axisText}
                      fontSize={chartFont.tick.size} fontFamily={chartFont.tick.family}>{tf(t)}</text>
              </g>
            ))}
            {rows.map((r) => {
              const yy = y(r.key) ?? 0;
              const bh = Math.min(BAR, y.bandwidth());
              const cy = yy + y.bandwidth() / 2;
              const w = Math.max(0, x(r.value) - x0);
              const rr = Math.min(barStyle.radius / 2, w / 2, bh / 2); // 4px rounded data end
              const on = highlightKey === null || highlightKey === r.key;
              const fill = hover === r.key ? chartColors.primaryText : chartColors.primary;
              const label = lines.get(r.key) ?? [r.label];
              const content = <BarTip r={r} units={units} valueLabel={valueLabel} />;
              return (
                <g key={r.key}
                   onPointerMove={(e) => { show(e, content); setHover(r.key); }}
                   onPointerLeave={leave}
                   tabIndex={0}
                   onFocus={(e) => { showAt(e.currentTarget, content); setHover(r.key); }}
                   onBlur={leave}
                   style={{ cursor: 'default', outline: 'none' }}>
                  <rect x={-left} y={yy - 6} width={left + innerW + M.right} height={y.bandwidth() + 12} fill="transparent" />
                  <text x={-LABEL_GAP} y={cy - 2 - (label.length - 1) * LABEL_LINE / 2} textAnchor="end" fill="var(--txt)" fontSize={13.5} fontFamily="var(--font-body)" fontWeight={500}>
                    {label.map((l, i) => <tspan key={i} x={-LABEL_GAP} dy={i ? LABEL_LINE : 0}>{l}</tspan>)}
                    {label.length > 1 && <title>{r.label}</title>}
                  </text>
                  <text x={-LABEL_GAP} y={cy + 13 + (label.length - 1) * LABEL_LINE / 2} textAnchor="end" fill="var(--dim)" fontSize={11} fontFamily="var(--font-body)">
                    n = {fmtInt(r.n)}
                  </text>
                  <path
                    d={`M${x0},${cy - bh / 2} h${Math.max(0, w - rr)} a${rr},${rr} 0 0 1 ${rr},${rr} v${bh - 2 * rr} a${rr},${rr} 0 0 1 ${-rr},${rr} h${-Math.max(0, w - rr)} z`}
                    fill={fill}
                    opacity={on ? 1 : 0.35}
                  />
                  <text x={x0 + w + 10} y={cy + 5} fill="var(--txt)" fontSize={14} fontFamily="var(--font-display)" fontWeight={500}>
                    {fmtValue(units, r.value)}
                  </text>
                </g>
              );
            })}
            <line x1={x0} x2={x0} y1={0} y2={plotH} stroke={chartColors.axisText} />
          </g>
        </svg>
      )}
      <Tooltip tip={tip} width={width} />
    </div>
  );
}

function BarTip({ r, units, valueLabel }: { r: BarRow; units: string | null; valueLabel: string }) {
  return (
    <div>
      <div className="tip-v">{fmtValue(units, r.value)}</div>
      <div className="tip-l">{r.label}</div>
      <div className="tip-s">{t.barTip(valueLabel, fmtInt(r.n))}</div>
    </div>
  );
}
